/**
 * Organization Compliance Runs Tab
 *
 * Lists compliance runs for this org with search and filters.
 */

import { useState } from 'react';
import { AdminCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import {
  Search,
  MoreHorizontal,
  Eye,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { Org, ComplianceRunSummary } from '@/integrations/firebase/types';
import { format } from 'date-fns';

interface OrgRunsTabProps {
  org: Org;
}

// Compliance runs - will be loaded from Firestore
// TODO: Replace with real Firestore query for org-scoped compliance runs
const complianceRuns: ComplianceRunSummary[] = [];

export function OrgRunsTab({ org }: OrgRunsTabProps) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Filter runs based on search and status
  const filteredRuns = complianceRuns.filter((run) => {
    const matchesSearch =
      run.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      run.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || run.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: ComplianceRunSummary['status']) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/40">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/40">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case 'running':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/40">
            <Clock className="h-3 w-3 mr-1 animate-pulse" />
            Running
          </Badge>
        );
      case 'queued':
        return (
          <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/40">
            <Clock className="h-3 w-3 mr-1" />
            Queued
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleViewRun = (runId: string) => {
    toast({
      title: 'Not Implemented',
      description: 'Run detail view will open in a modal',
    });
  };

  const handleRerunCheck = (runId: string) => {
    toast({
      title: 'Not Implemented',
      description: 'Re-run will be wired to backend',
    });
  };

  const handleDownloadBundle = (runId: string) => {
    toast({
      title: 'Not Implemented',
      description: 'Debug bundle download will be wired to backend',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-white mb-1">Compliance Runs</h2>
        <p className="text-sm text-gray-400">
          View and manage compliance check runs for this organization
        </p>
      </div>

      {/* Filters */}
      <AdminCard>
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by job title or run ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </AdminCard>

      {/* Runs Table */}
      <AdminCard className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-[hsl(220,15%,20%)] hover:bg-transparent">
              <TableHead className="text-gray-400">Job</TableHead>
              <TableHead className="text-gray-400">Status</TableHead>
              <TableHead className="text-gray-400">Results</TableHead>
              <TableHead className="text-gray-400">Started</TableHead>
              <TableHead className="text-gray-400">Triggered By</TableHead>
              <TableHead className="text-gray-400 w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRuns.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-gray-500 py-8"
                >
                  No compliance runs found
                </TableCell>
              </TableRow>
            ) : (
              filteredRuns.map((run) => (
                <TableRow
                  key={run.id}
                  className="border-[hsl(220,15%,20%)] hover:bg-[hsl(220,15%,12%)]"
                >
                  <TableCell>
                    <div>
                      <p className="text-white font-medium">{run.jobTitle}</p>
                      <p className="text-xs text-gray-500">{run.id}</p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(run.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-400">{run.passedRules} ✓</span>
                      <span className="text-red-400">{run.failedRules} ✗</span>
                      {run.warningRules > 0 && (
                        <span className="text-amber-400">
                          {run.warningRules}{' '}
                          <AlertTriangle className="inline h-3 w-3" />
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-400">
                    {run.startedAt
                      ? format(run.startedAt.toDate(), 'MMM d, h:mm a')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        run.triggeredBy === 'admin'
                          ? 'border-amber-500/40 text-amber-400'
                          : 'border-gray-600 text-gray-400'
                      }`}
                    >
                      {run.triggeredBy === 'admin' ? 'Admin' : 'User'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-white hover:bg-[hsl(220,15%,20%)]"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,20%)]"
                      >
                        <DropdownMenuItem
                          onClick={() => handleViewRun(run.id)}
                          className="text-gray-300 hover:text-white hover:bg-[hsl(220,15%,20%)] cursor-pointer"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleRerunCheck(run.id)}
                          className="text-gray-300 hover:text-white hover:bg-[hsl(220,15%,20%)] cursor-pointer"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Re-run Check
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDownloadBundle(run.id)}
                          className="text-gray-300 hover:text-white hover:bg-[hsl(220,15%,20%)] cursor-pointer"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Debug Bundle
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </AdminCard>
    </div>
  );
}
